import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../api';
import { useDropzone } from 'react-dropzone';
import {
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  LinearProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FolderIcon from '@mui/icons-material/Folder';
import SearchIcon from '@mui/icons-material/Search';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

function Cases() {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [newlyCreatedCase, setNewlyCreatedCase] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    client_name: '',
    case_number: '',
    user_role: '',
    status: 'open'
  });

  useEffect(() => {
    fetchCases();
  }, []);

  const fetchCases = async () => {
    try {
      setLoading(true);
      const response = await apiService.getCases();
      setCases(response.data.cases);
      setError('');
    } catch (err) {
      setError('Failed to fetch cases');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCase = async () => {
    try {
      const response = await apiService.createCase(formData);
      const createdCase = response.data.case;
      
      setOpenDialog(false);
      setFormData({
        title: '',
        description: '',
        client_name: '',
        case_number: '',
        user_role: '',
        status: 'open'
      });
      
      // Open upload dialog for the newly created case
      setNewlyCreatedCase(createdCase);
      setOpenUploadDialog(true);
      
      fetchCases();
    } catch (err) {
      setError('Failed to create case');
      console.error(err);
    }
  };

  const handleDeleteCase = async (caseId, e) => {
    if (e) e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this case?')) {
      try {
        await apiService.deleteCase(caseId);
        fetchCases();
      } catch (err) {
        setError('Failed to delete case');
        console.error(err);
      }
    }
  };

  const onDrop = async (acceptedFiles) => {
    if (!newlyCreatedCase) return;

    setUploading(true);
    const newFiles = acceptedFiles.map(file => ({
      file,
      name: file.name,
      status: 'uploading',
      progress: 0
    }));

    setUploadedFiles(prev => [...prev, ...newFiles]);

    for (const fileItem of newFiles) {
      try {
        await apiService.uploadDocument(newlyCreatedCase.id, fileItem.file);
        
        setUploadedFiles(prev => prev.map(f => 
          f.name === fileItem.name 
            ? { ...f, status: 'completed', progress: 100 }
            : f
        ));
      } catch (err) {
        console.error('Error uploading file:', err);
        setUploadedFiles(prev => prev.map(f => 
          f.name === fileItem.name 
            ? { ...f, status: 'error', progress: 0 }
            : f
        ));
      }
    }

    setUploading(false);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt']
    },
    multiple: true
  });

  const handleCloseUploadDialog = () => {
    setOpenUploadDialog(false);
    setNewlyCreatedCase(null);
    setUploadedFiles([]);
  };

  const handleFinishAndView = () => {
    if (newlyCreatedCase) {
      navigate(`/case/${newlyCreatedCase.id}`);
    }
    handleCloseUploadDialog();
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'primary',
      'in-progress': 'warning',
      closed: 'success',
      archived: 'default'
    };
    return colors[status] || 'default';
  };

  const filteredCases = cases.filter(c => {
    const matchesSearch = c.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         c.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         c.case_number.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || c.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
            Case Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage and track all your legal cases
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
          size="large"
        >
          New Case
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Filters and Search */}
      <Card sx={{ mb: 3, boxShadow: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                placeholder="Search cases..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Filter by Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Cases</MenuItem>
                <MenuItem value="open">Open</MenuItem>
                <MenuItem value="in-progress">In Progress</MenuItem>
                <MenuItem value="closed">Closed</MenuItem>
                <MenuItem value="archived">Archived</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={(e, newMode) => newMode && setViewMode(newMode)}
                  size="small"
                >
                  <ToggleButton value="grid">
                    <ViewModuleIcon />
                  </ToggleButton>
                  <ToggleButton value="table">
                    <ViewListIcon />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {filteredCases.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 8 }}>
          <FolderIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {searchTerm || statusFilter !== 'all' ? 'No cases found' : 'No cases yet'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {searchTerm || statusFilter !== 'all' ? 'Try adjusting your filters' : 'Create your first case to get started'}
          </Typography>
          {!searchTerm && statusFilter === 'all' && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenDialog(true)}>
              Create Case
            </Button>
          )}
        </Card>
      ) : viewMode === 'grid' ? (
        <Grid container spacing={3}>
          {filteredCases.map((caseItem) => (
            <Grid item xs={12} md={6} lg={4} key={caseItem.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': { boxShadow: 6, transform: 'translateY(-4px)' }
                }}
                onClick={() => navigate(`/case/${caseItem.id}`)}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" component="h2" noWrap sx={{ fontWeight: 600 }}>
                      {caseItem.title}
                    </Typography>
                    <Chip
                      label={caseItem.status}
                      size="small"
                      color={getStatusColor(caseItem.status)}
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <strong>Client:</strong> {caseItem.client_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <strong>Case #:</strong> {caseItem.case_number}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical'
                    }}
                  >
                    {caseItem.description}
                  </Typography>
                </CardContent>
                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                  <Button size="small" onClick={() => navigate(`/case/${caseItem.id}`)}>
                    View Details
                  </Button>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => handleDeleteCase(caseItem.id, e)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
          <Table>
            <TableHead sx={{ bgcolor: 'grey.100' }}>
              <TableRow>
                <TableCell><strong>Case Name</strong></TableCell>
                <TableCell><strong>Client</strong></TableCell>
                <TableCell><strong>Case Number</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCases.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((caseItem) => (
                <TableRow
                  key={caseItem.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/case/${caseItem.id}`)}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{caseItem.title}</Typography>
                  </TableCell>
                  <TableCell>{caseItem.client_name}</TableCell>
                  <TableCell>{caseItem.case_number}</TableCell>
                  <TableCell>
                    <Chip
                      label={caseItem.status}
                      size="small"
                      color={getStatusColor(caseItem.status)}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => handleDeleteCase(caseItem.id, e)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={filteredCases.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </TableContainer>
      )}

      {/* Create Case Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Case</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Case Title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Client Name"
            value={formData.client_name}
            onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Case Number"
            value={formData.case_number}
            onChange={(e) => setFormData({ ...formData, case_number: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Your Role in Case *"
            value={formData.user_role}
            onChange={(e) => setFormData({ ...formData, user_role: e.target.value })}
            margin="normal"
            required
            placeholder="e.g., for the motion, against the motion, for the plaintiff, for the defendant"
            helperText="Required: Specify your role so AI can provide tailored analysis"
          />
          <TextField
            fullWidth
            select
            label="Status"
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            margin="normal"
          >
            <MenuItem value="open">Open</MenuItem>
            <MenuItem value="in-progress">In Progress</MenuItem>
            <MenuItem value="closed">Closed</MenuItem>
            <MenuItem value="archived">Archived</MenuItem>
          </TextField>
          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            margin="normal"
            multiline
            rows={4}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateCase} 
            variant="contained"
            disabled={!formData.title || !formData.client_name || !formData.case_number || !formData.user_role}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Documents Dialog */}
      <Dialog 
        open={openUploadDialog} 
        onClose={handleCloseUploadDialog} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Box>
            <Typography variant="h6">Upload Case Documents</Typography>
            {newlyCreatedCase && (
              <Typography variant="body2" color="text.secondary">
                {newlyCreatedCase.title} - {newlyCreatedCase.case_number}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload all documents related to this case. They will be available in the Analyzer, Research Vault, and AI Paralegal.
            </Typography>
            
            {/* Drag and Drop Zone */}
            <Box
              {...getRootProps()}
              sx={{
                border: '2px dashed',
                borderColor: isDragActive ? 'primary.main' : 'grey.300',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                cursor: 'pointer',
                bgcolor: isDragActive ? 'action.hover' : 'background.paper',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover'
                }
              }}
            >
              <input {...getInputProps()} />
              <UploadFileIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                {isDragActive ? 'Drop files here' : 'Drag & drop files here'}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                or click to browse
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Supported formats: PDF, DOC, DOCX, TXT
              </Typography>
            </Box>
          </Box>

          {/* Uploaded Files List */}
          {uploadedFiles.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Uploaded Files ({uploadedFiles.length})
              </Typography>
              <List sx={{ bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                {uploadedFiles.map((file, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      {file.status === 'completed' ? (
                        <CheckCircleIcon color="success" />
                      ) : file.status === 'error' ? (
                        <DescriptionIcon color="error" />
                      ) : (
                        <DescriptionIcon color="action" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={file.name}
                      secondary={
                        file.status === 'uploading' ? (
                          <LinearProgress sx={{ mt: 0.5 }} />
                        ) : file.status === 'completed' ? (
                          'Upload complete'
                        ) : file.status === 'error' ? (
                          'Upload failed'
                        ) : null
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUploadDialog}>
            Skip for Now
          </Button>
          <Button 
            onClick={handleFinishAndView} 
            variant="contained"
            disabled={uploading}
          >
            {uploadedFiles.length > 0 ? 'Finish & View Case' : 'View Case'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Cases;